import { useContext, useEffect, useState } from 'react'
import { observer } from 'mobx-react-lite'
import styled from 'styled-components'

import type { components } from '../../constants/schema'

import { HttpReq, toast } from '../../utils'

import { StoresContext } from '../../provider/StoreProvider'

import { Wrap } from '../../components/container/Wrap'
import { Content } from '../../components/container/Content'
import { Header } from '../../components/header'
import { InfinityScroll } from '../../components/pagingList/InfinityScroll'
import { ParamOption } from '../../components/paramOption'
import { ContentList, ContentItem } from './components'

type ArticleListResponse = components['schemas']['ResListArticle']
type ArticleCategoryResponse = components['schemas']['ResListArticleCategory']

const SIZE = 50

export const Contents = observer(() => {
  const { commonStore } = useContext(StoresContext)

  const [categoryId, setCategoryId] = useState<number | undefined>(undefined)
  const [articleList, setArticleList] = useState<ArticleListResponse | null>(null)
  const [categories, setCategories] = useState<ArticleCategoryResponse | null>(null)

  useEffect(() => {
    getArticleCategories()
    commonStore.setShowTab(true)
  }, [])

  useEffect(() => {
    if (location.pathname.includes('/contents')) commonStore.setActiveTab('contents')
  }, [location])

  useEffect(() => {
    getArticleList({ categoryId })
  }, [categoryId])

  const getArticleList = async ({
    categoryId,
    offset: _offset,
  }: {
    categoryId: number | undefined
    offset?: number
  }) => {
    try {
      const offset = _offset ?? 0

      const response = await HttpReq<ArticleListResponse>({
        method: 'GET',
        url: '/article',
        data: { categoryId, size: SIZE, offset: offset ? offset + SIZE : 0 },
      })

      setArticleList((prevState) =>
        prevState && offset ? { ...response, list: [...prevState.list, ...response.list] } : response,
      )
    } catch (err: unknown) {
      toast('콘텐츠 목록을 불러오는데 실패했어요.', err)
    }
  }

  const getArticleCategories = async () => {
    try {
      const response = await HttpReq<ArticleCategoryResponse>({
        method: 'GET',
        url: '/article/category',
      })

      setCategories(response)
    } catch (error) {
      toast('카테고리 목록을 불러오는데 실패했어요.')
    }
  }

  const refresh = async () => {
    await getArticleList({ categoryId })
  }

  const nextArticleList = async () => {
    await getArticleList({ categoryId, offset: (articleList?.offset ?? 0) + SIZE })
  }

  return (
    <Wrap>
      <Header type="SUB" title="콘텐츠" isBack={false} />
      <StyledContent id="infinity-scroll-target" isPullEvent={true} onPull={refresh}>
        {categories && (
          <ParamOption
            categoryData={{
              categoryId,
              setCategoryId,
              categoryList: categories.list,
            }}
          />
        )}
        <InfinityScroll length={articleList?.list.length ?? 0} isLast={!!articleList?.last} next={nextArticleList}>
          <ContentList>
            {articleList?.list.map(({ id, title, imageUri }) => (
              <ContentItem key={id} title={title ?? ''} imgUri={imageUri ?? ''} href={`/contents/${id}`} line={2} />
            ))}
          </ContentList>
        </InfinityScroll>
      </StyledContent>
    </Wrap>
  )
})

const StyledContent = styled(Content)`
  height: 100%;
  padding-top: 16px;
`
